/**
 * Calm-Style Landing Page for AdElevate
 * Inspired by Calm.com's actual design
 * Supports dynamic color themes: blue, purple, green
 */

import React, { useState, useEffect } from 'react';

// Theme color configurations
const themeColors = {
  blue: {
    primary: 'from-blue-500 to-sky-500',
    primaryHover: 'from-blue-600 to-sky-600',
    primarySolid: 'bg-blue-500',
    primarySolidHover: 'hover:bg-blue-600',
    accent: 'text-blue-500',
    accentLight: 'text-blue-400',
    accentDark: 'text-blue-600',
    gradient: 'from-blue-400 via-sky-500 to-blue-400',
    bgGlow1: 'rgba(59,130,246,0.15)',
    bgGlow2: 'rgba(14,165,233,0.15)',
    testimonialBg: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 50%, #E0F2FE 100%)',
    featureIcon1: 'from-blue-500 to-sky-500',
    featureIcon2: 'from-cyan-500 to-blue-500',
    featureIcon3: 'from-sky-500 to-blue-500',
    starColor: 'text-blue-400',
    pricingAccent: 'text-blue-400',
    pricingBadge: 'bg-blue-500/90',
    ctaGlow: 'shadow-blue-500/30 hover:shadow-blue-500/50',
    navCta: 'hover:bg-blue-50',
  },
  purple: {
    primary: 'from-purple-500 to-pink-500',
    primaryHover: 'from-purple-600 to-pink-600',
    primarySolid: 'bg-purple-500',
    primarySolidHover: 'hover:bg-purple-600',
    accent: 'text-purple-500',
    accentLight: 'text-purple-400',
    accentDark: 'text-purple-600',
    gradient: 'from-purple-400 via-pink-500 to-purple-400',
    bgGlow1: 'rgba(168,85,247,0.15)',
    bgGlow2: 'rgba(236,72,153,0.15)',
    testimonialBg: 'linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 50%, #FDF2F8 100%)',
    featureIcon1: 'from-purple-500 to-pink-500',
    featureIcon2: 'from-pink-500 to-purple-500',
    featureIcon3: 'from-violet-500 to-purple-500',
    starColor: 'text-purple-400',
    pricingAccent: 'text-purple-400',
    pricingBadge: 'bg-purple-500/90',
    ctaGlow: 'shadow-purple-500/30 hover:shadow-purple-500/50',
    navCta: 'hover:bg-purple-50',
  },
  green: {
    primary: 'from-green-500 to-emerald-500',
    primaryHover: 'from-green-600 to-emerald-600',
    primarySolid: 'bg-green-500',
    primarySolidHover: 'hover:bg-green-600',
    accent: 'text-green-500',
    accentLight: 'text-green-400',
    accentDark: 'text-green-600',
    gradient: 'from-green-400 via-emerald-500 to-green-400',
    bgGlow1: 'rgba(34,197,94,0.15)',
    bgGlow2: 'rgba(16,185,129,0.15)',
    testimonialBg: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 50%, #D1FAE5 100%)',
    featureIcon1: 'from-green-500 to-emerald-500',
    featureIcon2: 'from-emerald-500 to-teal-500',
    featureIcon3: 'from-teal-500 to-green-500',
    starColor: 'text-green-400',
    pricingAccent: 'text-green-400',
    pricingBadge: 'bg-green-500/90',
    ctaGlow: 'shadow-green-500/30 hover:shadow-green-500/50',
    navCta: 'hover:bg-green-50',
  }
};

const CalmStyleHero = ({ onGetStarted, showcaseVideoUrl, colorTheme = 'blue' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const colors = themeColors[colorTheme] || themeColors.blue;
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-900">
      {/* Background Image/Video */}
      <div className="absolute inset-0">
        {/* Gradient overlay with theme colors */}
        <div 
          className="absolute inset-0 z-10"
          style={{
            background: `linear-gradient(
              180deg, 
              rgba(15, 23, 42, 0.3) 0%, 
              rgba(15, 23, 42, 0.1) 30%,
              ${colors.bgGlow1} 60%,
              rgba(15, 23, 42, 0.4) 100%
            )`
          }}
        />
        
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80')`,
            filter: 'brightness(0.7) saturate(0.9)'
          }}
        />
        
        {/* Animated gradient orbs */}
        <div className="absolute inset-0 z-5 overflow-hidden">
          <div 
            className="absolute -top-1/4 -left-1/4 w-[800px] h-[800px] rounded-full opacity-30"
            style={{
              background: `radial-gradient(circle, ${colors.bgGlow1} 0%, transparent 70%)`,
              animation: 'float 20s ease-in-out infinite'
            }}
          />
          <div 
            className="absolute -bottom-1/4 -right-1/4 w-[600px] h-[600px] rounded-full opacity-20"
            style={{
              background: `radial-gradient(circle, ${colors.bgGlow2} 0%, transparent 70%)`,
              animation: 'float 15s ease-in-out infinite reverse'
            }}
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="relative z-20 px-6 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 bg-gradient-to-br ${colors.primary} rounded-xl flex items-center justify-center shadow-lg`}>
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <span className="text-white text-xl font-semibold tracking-tight">AdElevate</span>
          </div>
          
          {/* Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-white/80 hover:text-white transition-colors text-sm font-medium">Features</a>
            <a href="#pricing" className="text-white/80 hover:text-white transition-colors text-sm font-medium">Pricing</a>
            <a href="#testimonials" className="text-white/80 hover:text-white transition-colors text-sm font-medium">Stories</a>
          </div>
          
          {/* CTA */}
          <div className="flex items-center gap-4">
            <button className="text-white/80 hover:text-white transition-colors text-sm font-medium">
              Sign In
            </button>
            <button 
              onClick={onGetStarted}
              className={`px-5 py-2.5 bg-white text-slate-900 rounded-full text-sm font-semibold ${colors.navCta} transition-all shadow-lg`}
            >
              Try Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-20 flex flex-col items-center justify-center min-h-[calc(100vh-100px)] px-6 text-center">
        {/* Main Headline */}
        <h1 
          className={`text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 leading-[1.1] tracking-tight transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            textShadow: '0 4px 30px rgba(0,0,0,0.3)'
          }}
        >
          Create your ads.
          <br />
          <span 
            className={`text-transparent bg-clip-text bg-gradient-to-r ${colors.gradient}`}
            style={{
              backgroundSize: '200% 200%',
              animation: 'shimmer 3s ease-in-out infinite'
            }}
          >
            Change your business.
          </span>
        </h1>

        {/* Subheadline */}
        <p 
          className={`text-xl md:text-2xl text-white/80 max-w-2xl mb-10 leading-relaxed transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Marketing is hard. Creating content doesn't need to be. 
          Generate stunning ads in 60 seconds with AI that understands your brand.
        </p>

        {/* CTA Button */}
        <div 
          className={`transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button 
            onClick={onGetStarted}
            className={`group relative px-10 py-4 bg-gradient-to-r ${colors.primary} text-white text-lg font-semibold rounded-full shadow-2xl ${colors.ctaGlow} hover:scale-105 transition-all duration-300`}
            data-testid="calm-hero-cta"
          >
            <span className="relative z-10">Start Creating for Free</span>
            <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${colors.primaryHover} opacity-0 group-hover:opacity-100 transition-opacity`} />
          </button>
          
          <p className="mt-4 text-white/60 text-sm">
            No credit card required - 100 free credits
          </p>
        </div>

        {/* Video Preview Card */}
        <div 
          className={`mt-16 w-full max-w-4xl transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div 
            className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-2 shadow-2xl border border-white/20 hover:border-white/30 transition-all group cursor-pointer"
            onClick={() => setIsVideoPlaying(true)}
          >
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
              {showcaseVideoUrl && isVideoPlaying ? (
                <video 
                  src={showcaseVideoUrl}
                  autoPlay 
                  controls
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      <div className={`absolute inset-0 w-24 h-24 -m-2 rounded-full border-2 ${colors.accent.replace('text-', 'border-')}/30 animate-ping`} style={{ animationDuration: '2s' }} />
                      <div className={`absolute inset-0 w-20 h-20 rounded-full border ${colors.accent.replace('text-', 'border-')}/40 animate-pulse`} />
                      
                      <div className={`relative w-20 h-20 bg-gradient-to-br ${colors.primary} rounded-full flex items-center justify-center shadow-2xl ${colors.ctaGlow} group-hover:scale-110 transition-transform`}>
                        <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-slate-700 shadow-lg">
                    AI-Powered
                  </div>
                  <div className={`absolute top-4 right-4 px-3 py-1.5 ${colors.pricingBadge} backdrop-blur-sm rounded-full text-xs font-medium text-white shadow-lg`}>
                    60 Seconds
                  </div>
                  <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-slate-700 shadow-lg">
                    Watch the magic
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Stats Bar */}
      <div 
        className={`relative z-20 py-8 transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">10K+</div>
              <div className="text-sm text-white/60">Happy Creators</div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/20" />
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">1M+</div>
              <div className="text-sm text-white/60">Ads Generated</div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/20" />
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">99%</div>
              <div className="text-sm text-white/60">Time Saved</div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/20" />
            <div className="text-center flex items-center gap-2">
              <div className={`flex ${colors.starColor}`}>
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <div className="text-sm text-white/60">2M+ Reviews</div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(30px, 30px); }
        }
        
        @keyframes shimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
};

// Features Section
const CalmFeaturesSection = ({ colorTheme = 'blue' }) => {
  const colors = themeColors[colorTheme] || themeColors.blue;
  
  const features = [
    {
      icon: '\u26A1',
      title: 'Create Faster',
      description: 'Generate stunning ads in 60 seconds. No more waiting days for designers.',
      color: colors.featureIcon1
    },
    {
      icon: '\uD83C\uDFAF',
      title: 'Convert More',
      description: 'AI-optimized content that speaks to your audience and drives results.',
      color: colors.featureIcon2
    },
    {
      icon: '\uD83D\uDCB0',
      title: 'Save More',
      description: 'Cut marketing costs by 99%. No agencies, no freelancers needed.',
      color: colors.featureIcon3
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-4">
          We're here to help you{' '}
          <span className={colors.accent}>grow.</span>
        </h2>
        <p className="text-xl text-slate-600 text-center max-w-2xl mx-auto mb-16">
          Everything you need to create marketing that works.
        </p>
        
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`group p-8 bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-slate-100 hover:border-${colorTheme === 'blue' ? 'blue' : colorTheme === 'purple' ? 'purple' : 'green'}-200 hover:-translate-y-2`}
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-3xl mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {feature.description}
              </p>
              <a href="#" className={`inline-flex items-center gap-2 mt-4 ${colors.accent} font-medium hover:gap-3 transition-all`}>
                Learn More
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const CalmTestimonialsSection = ({ colorTheme = 'blue' }) => {
  const colors = themeColors[colorTheme] || themeColors.blue;
  
  const testimonials = [
    {
      quote: "AdElevate transformed our marketing. We went from spending days on campaigns to having everything ready in minutes.",
      author: "Sarah Chen",
      role: "Marketing Director",
      company: "TechStart Inc"
    },
    {
      quote: "The AI understands our brand voice perfectly. Every ad feels authentic and on-brand.",
      author: "Michael Rodriguez",
      role: "Founder",
      company: "GrowthLabs"
    },
    {
      quote: "We saved $5,000/month on agency fees. The ROI was immediate.",
      author: "Emily Watson",
      role: "CMO",
      company: "ScaleUp Co"
    }
  ];

  return (
    <section 
      className="py-24 relative overflow-hidden"
      style={{ background: colors.testimonialBg }}
    >
      <div className={`absolute top-0 left-0 w-96 h-96 bg-${colorTheme === 'blue' ? 'blue' : colorTheme === 'purple' ? 'purple' : 'green'}-200/30 rounded-full blur-3xl`} />
      <div className={`absolute bottom-0 right-0 w-96 h-96 bg-${colorTheme === 'blue' ? 'sky' : colorTheme === 'purple' ? 'pink' : 'emerald'}-200/30 rounded-full blur-3xl`} />
      
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-4">
          Over 2 million{' '}
          <span className={colors.accent}>5-star</span> reviews.
        </h2>
        <p className="text-xl text-slate-600 text-center max-w-2xl mx-auto mb-16">
          Join thousands of marketers who've transformed their workflow.
        </p>
        
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="p-8 bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white"
            >
              <div className={`flex ${colors.starColor} mb-4`}>
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <blockquote className="text-lg text-slate-700 mb-6 italic">
                "{testimonial.quote}"
              </blockquote>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 bg-gradient-to-br ${colors.primary} rounded-full flex items-center justify-center text-white font-bold`}>
                  {testimonial.author.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.author}</div>
                  <div className="text-sm text-slate-500">{testimonial.role}, {testimonial.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const CalmPricingSection = ({ colorTheme = 'blue' }) => {
  const colors = themeColors[colorTheme] || themeColors.blue;
  
  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Start your free trial of{' '}
          <span className={`text-transparent bg-clip-text bg-gradient-to-r ${colors.gradient}`}>
            AdElevate Premium.
          </span>
        </h2>
        <p className="text-xl text-white/70 max-w-2xl mx-auto mb-12">
          Get 100 free credits to experience the future of marketing.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {/* Yearly Plan */}
          <div className="p-8 bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 text-left">
            <div className={`text-sm font-medium ${colors.pricingAccent} mb-2`}>MOST POPULAR</div>
            <div className="text-white font-semibold mb-4">Yearly</div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-4xl font-bold text-white">$79.99</span>
              <span className="text-white/60">/yr</span>
            </div>
            <div className={`${colors.pricingAccent} text-sm mb-6`}>$6.67/mo - Save 60%</div>
            <button className={`w-full py-3 bg-gradient-to-r ${colors.primary} text-white font-semibold rounded-xl hover:${colors.primaryHover} transition-all shadow-lg`}>
              Start 14-Day Free Trial
            </button>
          </div>
          
          {/* Monthly Plan */}
          <div className="p-8 bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 text-left">
            <div className="text-sm font-medium text-white/40 mb-2">&nbsp;</div>
            <div className="text-white font-semibold mb-4">Monthly</div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-4xl font-bold text-white">$16.99</span>
              <span className="text-white/60">/mo</span>
            </div>
            <div className="text-white/40 text-sm mb-6">Flexible billing</div>
            <button className="w-full py-3 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all">
              Start Free Trial
            </button>
          </div>
        </div>
        
        <p className="mt-6 text-white/50 text-sm">
          Cancel anytime - No commitment required
        </p>
      </div>
    </section>
  );
};

// Theme Switcher Component
const ThemeSwitcher = ({ colorTheme, setColorTheme }) => {
  const themes = [
    { id: 'blue', name: 'Blue', color: 'bg-blue-500' },
    { id: 'purple', name: 'Purple', color: 'bg-purple-500' },
    { id: 'green', name: 'Green', color: 'bg-green-500' }
  ];

  return (
    <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-full shadow-2xl border border-slate-200 dark:border-slate-700">
      <span className="text-sm font-medium text-slate-500 mr-2">Theme:</span>
      {themes.map(theme => (
        <button
          key={theme.id}
          onClick={() => setColorTheme(theme.id)}
          className={`w-8 h-8 rounded-full ${theme.color} transition-all ${
            colorTheme === theme.id 
              ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' 
              : 'opacity-60 hover:opacity-100'
          }`}
          title={theme.name}
          data-testid={`theme-switch-${theme.id}`}
        />
      ))}
    </div>
  );
};

// Full Calm-Style Landing Page with Theme Support
const CalmStyleLandingPage = ({ onGetStarted, showcaseVideoUrl, initialColorTheme = 'blue' }) => {
  const [colorTheme, setColorTheme] = useState(initialColorTheme);

  return (
    <div className="min-h-screen">
      <ThemeSwitcher colorTheme={colorTheme} setColorTheme={setColorTheme} />
      <CalmStyleHero onGetStarted={onGetStarted} showcaseVideoUrl={showcaseVideoUrl} colorTheme={colorTheme} />
      <CalmFeaturesSection colorTheme={colorTheme} />
      <CalmTestimonialsSection colorTheme={colorTheme} />
      <CalmPricingSection colorTheme={colorTheme} />
    </div>
  );
};

export default CalmStyleLandingPage;
export { CalmStyleHero, CalmFeaturesSection, CalmTestimonialsSection, CalmPricingSection, ThemeSwitcher };
